<template>
	<core-layout-content full>
		<!-- Headline -->
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("objects") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="computedPage"
				/>
			</template>
			<template slot="actions">
				<j-button v-can="'writeObjects'" @click="$modal.show('createObject')">
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_objects')"
					:value="searchTerm"
					@input="onSearch($event)"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
					>{{ $t("cancel") }}</j-button
				>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'name'">
								<router-link
									:to="{
										name: `ObjectsDetail`,
										params: { id: item.id }
									}"
									>{{ item.name ? item.name : "---" }}</router-link
								>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>

							<!-- Actions Field -->
							<template v-if="column.field === 'actions'">
								<div class="action">
									<!-- Delete -->
									<crm-icon
										name="delete"
										size="normal"
										color="cError"
										@click.native="showModal(item.id)"
										v-can="'writeCases'"
									/>
								</div>
							</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems ? listing.numberOfItems : 0"
				:perPage="itemsPerPage"
				:activePage="computedPage"
				@click="onPageChange($event)"
			/>
		</div>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="removeObject(selectedItemId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_object") }}</j-dialog
		>

		<!-- Create Object Modal -->
		<core-modal name="createObject" :headline="$t('create_object')">
			<div class="p4">
				<core-form-input
					name="name"
					componentName="j-base-input-text"
					:label="$t('name')"
					v-model="form.name"
				/>
				<j-button @click="createObject" class="mt6" :isLoading="isLoading">
					{{ $t("create") }}
				</j-button>
			</div>
		</core-modal>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JDialog from "@/components/JDialog/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"
import debounce from "lodash.debounce"

export default {
	components: {
		CoreLayoutContent,
		CoreFormInput,
		CoreModal,
		CoreLayoutHeadlineAndActions,
		JButton,
		CrmIcon,
		JCard,
		JDataTable,
		JPagination,
		CorePaginationInfo,
		JDialog,
		JBaseInputSearch
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			],

			selectedItemId: null,

			form: {
				name: null
			}
		}
	},

	methods: {
		onPageChange(pageIndex) {
			this.$store.commit("objects/SET_PAGE", pageIndex)
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value) {
			// const payload = value === null || value === "" ? null : value
			this.searchTerm = value
			this.$store.commit("objects/SET_PAGE", 1)
			this.readAll()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.$store.commit("objects/SET_PAGE", 1)
			this.onSearch()
		},

		async readAll() {
			const query = gql`
				query objects(
					$limit: Int
					$sort: sortInput
					$skip: Int
					$where: objectWhereInput
				) {
					objects(limit: $limit, sort: $sort, where: $where, skip: $skip) {
						totalCount
						list {
							id
							name
							createdAt
						}
					}
				}
			`

			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.computedPage - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			const where = this.computedWhere

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					sort,
					skip,
					limit,
					where
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.listing.list = res?.data?.objects?.list || []
			this.listing.numberOfItems = res?.data?.objects?.totalCount || 0
		},

		showModal(id) {
			this.selectedItemId = id
			this.$modal.show("j-dialog")
		},

		hideModal() {
			this.selectedItemId = null
			this.$modal.hide("j-dialog")
		},

		async removeObject(id) {
			const query = gql`
				mutation deleteObject($id: String!) {
					deleteEntity(id: $id) {
						status
						errors
						errors_2 {
							code
							message
						}
					}
				}
			`

			const res = await api({ query, variables: { id } })

			this.hideModal()

			if (res?.data?.deleteEntity?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deleteEntity?.errors ||
							res?.data?.deleteEntity?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("object_deleted")
					}
				})

			this.readAll()
		},

		async createObject() {
			const query = gql`
				mutation createObject($createObjectInput: createObjectInput!) {
					createObject(createObjectInput: $createObjectInput) {
						status
						errors
					}
				}
			`

			this.isLoading = true

			const res = await api({
				query,
				variables: {
					createObjectInput: { nameFormat: this.form.name }
				}
			})

			this.isLoading = false

			this.$modal.hide("createObject")

			if (res?.data?.createObject?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.createObject?.errors ||
							res?.data?.createObject?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("object_created")
					}
				})

			this.readAll()
		}
	},

	computed: {
		computedList() {
			if (!this.listing.list) return []

			return this.listing.list.map(item => ({
				id: item.id,
				name: item.name,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		},

		computedPage() {
			return this.$store.state.objects.listing.page
		},

		computedWhere() {
			if (!this.searchTerm) return

			return {
				name_contains: this.searchTerm
			}
		}
	},

	created() {
		this.readAll()
	},

	beforeRouteLeave(to, from, next) {
		// If in same module , do nothing, do not reset page
		if (to.meta.featureId === from.meta.featureId) {
			next()
		} else {
			// if not in same module, reset page to 1
			this.$store.commit("objects/SET_PAGE", 1)
			next()
		}
	}
}
</script>
